
import {Vue, Component, Prop, Ref} from "vue-property-decorator";
import {getModule} from "vuex-module-decorators";
import {CompanyModule, OrderModule} from "@/store/modules/index";
import notifications from "@/lib/notifications";
import {IOrderParty} from "@/store/types";
import * as modals from "@/lib/modals";
import { REGEX_PHONE_REPLACE } from "@/consts";

const companyModule = getModule(CompanyModule);
const orderModule = getModule(OrderModule);

const initialParty: IOrderParty = {
  id: null,
  name: null,
  address: {
    streetLine1: null,
    streetLine2: null,
    city: null,
    state: null,
    zip: null
  },
  primaryPhone: null,
  secondaryPhone: null,
  email: null,
  location: undefined,
  standardizedAddress: undefined,
  agentId: null,
  deliveryForId: 0,
  displayId: null,
  addAsConsigneeContact: true
};

@Component({
  name: modals.companySelectorModal
})
export default class CompanySelectorModal extends Vue {
  assignId: boolean = false;
  visible: boolean = false;
  loading: boolean = false;
  changing: boolean = false;
  currentTab: number = 0;
  newParty: IOrderParty = initialParty;
  search: any = {
    id: null,
    companyName: null,
    address: null,
    phoneNumber: null,
    results: null,
    isLoading: false,
    fields: [
      { key: "agentId", label: "Id" },
      { key: "name", label: "Name" },
      { key: "address.streetLine1", label: "Address" },
      { key: "address.city", label: "City" },
      { key: "address.zip", label: "Zip" },
      { key: "primaryPhone", label: "Phone" }
    ],
    selectedCompany: null
  };

  mounted() {
    this.reset();
  }

  @Ref() readonly companyId: HTMLInputElement;

  get findingExistingTitle() {
    return `Find Existing ${this.title}`;
  }

  get createNewTitle() {
    return `Add New ${this.title}`;
  }

  get selectCompanyButtonEnabled() {
    if (this.search.selectedCompany) return true;

    return false;
  }

  get toggleAssignIdText() {
    if (!this.assignId) return "Assign Company Id";
    return "Auto-generate Company Id";
  }

  @Prop({default: 0})
  deliveryForId?: number;

  @Prop({default: ""})
  type: string;

  @Prop({default: ""})
  title: string;

  @Prop({default: true})
  canCreate: boolean;

  @Prop({default: false})
  isNewOrder: boolean;

  @Prop({default: ""})
  consigneeCompanyId: string;

  hideModal() {
    this.visible = false;
  }

  show() {
    this.visible = true;
  }

  focus() {
    this.companyId.focus();
  }

  rowSelected(items) {
    if (items.length > 0) {
      this.search.selectedCompany = items[0];
    } else {
      this.search.selectedCompany = null;
    }
  }

  executeSearch() {
    this.search.isLoading = true;
    this.search.deliveryForId = this.deliveryForId == -1 ? null : this.deliveryForId;

    if (this.type === "billto") this.search.includeBillTos = true;

    if (this.isNewOrder) this.search.excludeRestrictedNewOrderBillTos = true;

    companyModule
      .searchCompanies(this.search)
      .then(response => {
        this.search.results = response.data;
      })
      .catch(error => {
      })
      .finally(() => {
        this.search.isLoading = false;
      });
  }

  restrictToDigits(e: KeyboardEvent) {
    if (!/\d/.test(e.key)) {
      e.preventDefault();
    }
  }

  formatPhone(value: string): string {
    const x = value.replace(/\D/g, '').match(REGEX_PHONE_REPLACE);
    return !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
  }

  acceptPrimaryPhone(value: string) {
    this.newParty.primaryPhone = this.formatPhone(value);
  }

  acceptSecondaryPhone(value: string) {
    this.newParty.secondaryPhone = this.formatPhone(value);
  }

  async create() {
    this.loading = true;
    let payload = {
      orderParty: {
        ...this.newParty,
        primaryPhone: this.newParty.primaryPhone ? this.newParty.primaryPhone.replace(/\D/g, '') : this.newParty.primaryPhone,
        secondaryPhone: this.newParty.secondaryPhone ? this.newParty.secondaryPhone.replace(/\D/g, '') : this.newParty.secondaryPhone,
      },
      isBillTo: this.type == "orderby",
      isConsignee: this.type == "consignee",
      isShipper: this.type == "shipper",
      deliveryForId: this.deliveryForId,
      consigneeCompanyId: this.consigneeCompanyId,
      addAsConsigneeContact: this.newParty.addAsConsigneeContact,
    };

    await companyModule
      .createCompany(payload)
      .then(result => {
        this.reset();
        this.changeCompany(result.data);
        notifications.success("Company created", "Company successfully created");
      })
      .catch(ex => {
        notifications.error("Company not created", "Failed to create company", ex);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  selectCompany() {
    this.changeCompany(this.search.selectedCompany);
  }

  changeCompany(selected) {
    this.$emit("company-selected", selected);
    this.hideModal();
  }

  reset() {
    this.currentTab = 0;
    this.search.phoneNumber = null;
    this.search.companyName = null;
    this.search.address = null;
    this.search.results = null;
    this.search.selectedCompany = null;
    this.loading = false;
    this.newParty = { ...initialParty };
    this.newParty.address = { ...initialParty.address };
  }

  switchToAdd() {
    this.currentTab = 1;
  }

  toggleAssignId() {
    this.assignId = !this.assignId;
    if (!this.assignId) this.newParty.id = null;
  }
}
